<template>
  <div style="position: relative">
    <!-- Loading Overlay -->
    <v-overlay v-model="activeLoading" class="align-center justify-center" :persistent="true" contained>
      <v-progress-circular color="primary" indeterminate size="48" width="3" />
    </v-overlay>
    <InfoBanner :show="showBanner" @close="showBanner = false" />
    <v-container class="containerFixedHeight d-flex align-center justify-center">
      <v-row align-center justify="center" wrap style="margin: 3rem auto 5rem auto; max-width: 600px">
        <v-col cols="12" v-if="welcomePageTitle" style="">
          <div class="welcome-header-mobile d-flex d-sm-none">
            <span
              ><template v-for="(seg, i) in titleSegments" :key="i"
                ><sup v-if="seg.trademark" class="trademark-symbol">{{ seg.text }}</sup
                ><template v-else>{{ seg.text }}</template></template
              ></span
            >
          </div>
          <div class="welcome-header d-none d-sm-flex justify-center" id="welcomeTitle">
            <span
              ><template v-for="(seg, i) in titleSegments" :key="i"
                ><sup v-if="seg.trademark" class="trademark-symbol">{{ seg.text }}</sup
                ><template v-else>{{ seg.text }}</template></template
              ></span
            >
          </div>
        </v-col>

        <v-col cols="12" v-if="partnerIntroMessage">
          <div id="welcomeContent" class="mb-6 text-center">{{ partnerIntroMessage }}</div>
        </v-col>

        <!-- Sign in section -->
        <v-col cols="12" style="max-width: 500px; margin-top: 48px">
          <div class="sign-in-section auth-card">
            <template v-if="isAetherLinkFlow">
              <h2 class="sign-in-title">Verify your email</h2>
              <p class="sign-in-subtitle">to complete account setup</p>
            </template>
            <template v-else>
              <h2 class="sign-in-title">Sign in</h2>
              <p class="sign-in-subtitle">to continue to Universal Learner Courses</p>
            </template>

            <v-form ref="form" v-model="valid" class="login-form">
              <v-text-field
                v-model="email"
                label="Email"
                type="email"
                :rules="emailRules"
                @update:modelValue="validateEmail"
                @keyup.enter="handleNext"
                @keydown.enter.prevent
                required
                variant="underlined"
                :readonly="isAetherLinkFlow"
                :disabled="isAetherLinkFlow"
                hide-details="auto"
              />
              <FieldError class="align-start" :error="emailNotFoundError" />

              <Button
                @click="handleNext"
                variant="primary"
                size="large"
                block
                rounded
                :disabled="!isValidEmail"
                data-testid="next-button"
                :style="{ 'font-weight': 'bold' }"
              >
                {{ isAetherLinkFlow ? 'Verify Email' : 'Continue with email' }}
              </Button>

              <template v-if="!isAetherLinkFlow">
                <div class="or-divider">or</div>

                <Button
                  @click="handleAsuriteLogin"
                  variant="gold"
                  size="large"
                  block
                  rounded
                  :style="{ 'font-weight': 'bold' }"
                >
                  Sign in with ASURITE
                </Button>
              </template>
            </v-form>

            <!-- Conditional "Don't have an account" section - hidden in aether-link flow -->
            <div v-if="isEligibleForSignUp && !isAetherLinkFlow" class="create-account-section">
              <p class="create-account-text">Don't have an account?</p>
              <a @click="handleCreateAccount" class="create-account-link">Create an account</a>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { useUserStore } from '@/stores/user';

import { DataLayerMixin } from '../mixins/dataLayerMixin.js';
import { FetchPartnerData } from '../mixins/fetchPartnerDataMixin';
import { AjaxLoader } from '../mixins/ajaxLoader';
import { Helper } from '../mixins/helpers.js';
import { Logger } from '../mixins/logger';
import InfoBanner from './InfoBannerComponent.vue';
import Button from './ui/Button.vue';
import FieldError from './ui/FieldError.vue';

export default defineComponent({
  components: { InfoBanner, Button, FieldError },

  data() {
    return {
      email: '',
      valid: false,
      showBanner: false, // ULC-3105: ASU email account message disabled
      emailNotFoundError: '',
      emailRules: [(v) => !!v || 'Email is required', (v) => /.+@.+\..+/.test(v) || 'Email must be valid']
    };
  },

  mixins: [Helper, DataLayerMixin, FetchPartnerData, AjaxLoader, Logger],

  computed: {
    welcomePageTitle() {
      const userStore = useUserStore();
      return userStore.welcomePageData?.welcomePageTitle;
    },
    // Split the title so trademark glyphs (® / ™) can be rendered as a
    // properly-sized superscript instead of a full-size baseline character.
    titleSegments() {
      const title = this.welcomePageTitle || '';
      return title.split(/([®™])/).reduce((segments, text) => {
        if (text) segments.push({ text, trademark: text === '®' || text === '™' });
        return segments;
      }, []);
    },
    isEligibleForSignUp() {
      const userStore = useUserStore();
      return userStore.welcomePageData?.isEligibleForSignUp;
    },
    isValidEmail() {
      return this.email && /.+@.+\..+/.test(this.email);
    },
    isAsuEmail() {
      return this.email.toLowerCase().endsWith('@asu.edu');
    },
    isAetherLinkFlow() {
      return this.$route.query.flow === 'aether-link';
    },
    isAccelerate() {
      return this.isAcceleratePartner(this.$conn.getPartnerName());
    },
    partnerIntroMessage() {
      if (!this.isAccelerate) return '';
      // Matches the special-casing WelcomeLoginComponent.introText() used to apply for
      // accelerate: the hardcoded ULC copy only on a non-ULC host, otherwise the
      // partner's own configured introduction.
      if (!this.isUlcPartner()) {
        return 'Jumpstart your path to higher education with Universal Learner Courses from Arizona State University.';
      }
      return ((this.$conn.partnerData || {}).welcomePageData || {}).introduction || '';
    },
    aetherLinkEmail() {
      return this.$route.query.email || '';
    }
  },

  methods: {
    validateEmail() {
      this.emailNotFoundError = '';
      this.$refs.form.validate();
    },

    async handleNext() {
      // handleNext called

      if (!this.isValidEmail) {
        // Email validation failed, returning
        return;
      }

      this.emailNotFoundError = '';

      try {
        // Aether-link flow: skip email existence check, go directly to custom OAuth
        if (this.isAetherLinkFlow) {
          this.$auth.login('custom_oauth', { email: this.email });
          return;
        }

        if (this.isAsuEmail) {
          // ASU email - skip email check, proceed directly with asurite authentication
          this.logInfo('ASU email detected, proceeding with asurite authentication');
          this.$auth.login('asurite');
        } else {
          // Non-ASU email - check if email exists in our system before proceeding.
          // Scope the check to the accelerate partner so an email that only exists
          // under a different partner can't slip through the OTP guard below.
          const partnerSubdomain = this.isAccelerate ? this.$conn.getPartnerName() : undefined;
          const emailExists = await this.$conn.checkEmailExists(this.email, partnerSubdomain);

          if (emailExists) {
            // Email exists, proceed with custom OAuth authentication
            this.logInfo('Non-ASU email exists in system, proceeding with custom OAuth');
            this.$auth.login('custom_oauth', { email: this.email });
          } else if (this.isAccelerate) {
            // Accelerate learners don't self-register; show a warning instead of routing to sign up
            this.logInfo('Non-ASU email not found for accelerate partner, showing contact-support message');
            this.emailNotFoundError =
              'Email not found. Check the spelling or contact your school administrator to confirm your sign-in email.';
          } else {
            // Email doesn't exist, redirect to registration with email prefilled
            this.logInfo('Non-ASU email not found in system, redirecting to registration');

            // Pass email via sessionStorage to keep it private (not visible in URL)
            sessionStorage.setItem('registrationEmail', this.email);
            this.$router.push({ name: 'Registerv2' });
          }
        }
      } catch (error) {
        this.logError('Error checking email existence:', error);
        // On error, proceed with normal flow as fallback
        if (this.isAetherLinkFlow) {
          this.$auth.login('custom_oauth', { email: this.email });
        } else if (this.isAsuEmail) {
          this.$auth.login('asurite');
        } else if (this.isAccelerate) {
          // Accelerate learners don't self-register - don't hand an unverified email to the OTP flow.
          // This is a check failure (network/5xx), not a not-found result, so it gets its own copy
          // rather than the misleading "email not found" message.
          this.logInfo('Unable to verify email for accelerate partner, showing retry message');
          this.emailNotFoundError = "We couldn't verify your email right now. Please try again in a moment.";
        } else {
          this.$auth.login('custom_oauth', { email: this.email });
        }
      } finally {
      }
    },

    handleCreateAccount() {
      try {
        this.asuoDataLayerClick('createLearner', '0', {
          section: 'create account'
        });
        // Pass email via sessionStorage to keep it private (not visible in URL)
        if (this.email) {
          sessionStorage.setItem('registrationEmail', this.email);
        }
        this.$router.push({ name: 'Registerv2' });
      } catch (error) {
        // console.error('Error in handleCreateAccount:', error);
        this.logError('handleCreateAccount error', error);
      }
    },

    handleAsuriteLogin() {
      this.logInfo('ASURITE login button clicked');
      this.$auth.login('asurite');
    }
  },

  created() {
    const userStore = useUserStore();
    if (!userStore.welcomePageData?.welcomePageTitle) {
      this.fetchPartnerData();
    }

    // Pre-fill email for aether-link flow
    if (this.isAetherLinkFlow && this.aetherLinkEmail) {
      this.email = this.aetherLinkEmail;
    }
  },

  mounted() {
    // Fix Vue event binding by manually attaching event listeners as fallback
    this.$nextTick(() => {
      // Fallback for ASURITE button - find button containing "ASURITE" text
      const asuriteBtn = Array.from(this.$el.querySelectorAll('button')).find(
        (btn) => btn.textContent && btn.textContent.includes('ASURITE')
      );

      if (asuriteBtn) {
        asuriteBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleAsuriteLogin();
        });
      }

      // Fallback for Next button if Vue binding fails
      const nextBtn = this.$el.querySelector('[data-testid="next-button"]');
      if (nextBtn) {
        // Check if the button already has the Vue click handler working
        const vueClickHandler = nextBtn._vei?.onClick;
        if (!vueClickHandler) {
          // Adding manual event listener for Next button
          nextBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.handleNext();
          });
        }
      }
    });
  }
});
</script>

<style lang="stylus">
.welcome-header {
  font-size: 36px;
  text-align: center;
  font-weight: 900;
  margin-bottom: 32px;
  color: #333;
}

/* Render ® / ™ as a small, raised superscript rather than a full-size
baseline glyph so it reads as a proper trademark mark. */
.trademark-symbol {
  font-size: 0.5em;
  font-weight: 600;
  vertical-align: super;
  line-height: 0;
  margin-left: 1px;
}

.welcome-header-mobile {
  font-size: 30px;
  text-align: center;
  font-weight: bold;
  margin-bottom: 32px;
  color: #333;
}

// Card look (background/border/padding/radius/shadow) comes from the shared .auth-card
// class in public/css/main.css; this rule only adds this section's own layout.
.sign-in-section {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.sign-in-title {
  font-size: 24px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: -0.72px;
  color: #000;
  margin: 0;
}

.sign-in-subtitle {
  font-size: 16px;
  line-height: 24px;
  color: #484848;
  margin: 0;
}

.login-form {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.create-account-section {
  text-align: center;
}

.create-account-text {
  font-size: 16px;
  color: #666;
  margin-bottom: 8px;
}

.create-account-link {
  color: #8C1D40;
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
}

.create-account-link:hover {
  text-decoration: underline;
}

.or-divider {
  text-align: center;
  color: #747474;
  font-size: 16px;
  line-height: 24px;
}

/* Button styles are now handled by the Button component itself */
.v-messages {
  text-align: left !important;
}

.v-messages__message {
  text-align: left !important;
}

@media (max-width: 600px) {
  .welcome-header {
    font-size: 30px;
  }
}
</style>
